import React, { createContext, useContext, useEffect, useState } from "react";
import { api } from "@/lib/api";

const SettingsContext = createContext(null);

const defaults = {
  brand_name: "Weddings by Mostash",
  tagline: "Weddings planned with taste, detail, and intention.",
  subtagline: "A visual portfolio of celebrations, spaces, stories, and moments curated by our planning team.",
  logo_url: "",
  hero_media: [],
  about_heading: "An agency built around detail, design, and execution.",
  about_body: "",
  locations_served: [],
  socials: { instagram: "", email: "", whatsapp: "" },
  colors: { background: "#0D0D0D", surface: "#1A1A1A", text_primary: "#F5F5F0", text_secondary: "#A3A3A3", accent: "#C5A059" },
  section_order: ["hero", "photofeed", "weddings", "reels", "moodboards", "stories", "about"],
  section_visibility: { hero: true, photofeed: true, weddings: true, reels: true, moodboards: true, stories: true, about: true },
  footer_copy: "© Weddings by Mostash. All rights reserved.",
};

const KNOWN_SECTIONS = ["hero", "photofeed", "weddings", "reels", "moodboards", "stories", "about"];

/**
 * Ensure any settings shape (including legacy ones from before a section
 * key was introduced) contains all KNOWN_SECTIONS in section_order and
 * section_visibility, in a stable position.
 */
function mergeSectionDefaults(raw) {
  const s = { ...defaults, ...raw };
  const savedOrder = Array.isArray(s.section_order) ? [...s.section_order] : [];
  for (let i = 0; i < KNOWN_SECTIONS.length; i++) {
    const key = KNOWN_SECTIONS[i];
    if (!savedOrder.includes(key)) {
      // Insert at the same index as in defaults, clamped to current length.
      const idx = Math.min(i, savedOrder.length);
      savedOrder.splice(idx, 0, key);
    }
  }
  const vis = { ...defaults.section_visibility, ...(s.section_visibility || {}) };
  for (const key of KNOWN_SECTIONS) if (vis[key] === undefined) vis[key] = true;
  return { ...s, section_order: savedOrder, section_visibility: vis };
}

export { mergeSectionDefaults };

export function SettingsProvider({ children }) {
  const [settings, setSettings] = useState(defaults);

  const load = async () => {
    try {
      const res = await api.get("/settings");
      setSettings(mergeSectionDefaults(res.data));
    } catch (_) { /* keep defaults */ }
  };

  useEffect(() => { load(); }, []);

  // Apply CSS variables from admin-editable colors
  useEffect(() => {
    const c = settings.colors || {};
    const r = document.documentElement.style;
    if (c.background) r.setProperty("--bg", c.background);
    if (c.surface) r.setProperty("--surface", c.surface);
    if (c.text_primary) r.setProperty("--text", c.text_primary);
    if (c.text_secondary) r.setProperty("--text-muted", c.text_secondary);
    if (c.accent) r.setProperty("--accent", c.accent);
  }, [settings.colors]);

  return (
    <SettingsContext.Provider value={{ settings, reload: load, setSettings }}>
      {children}
    </SettingsContext.Provider>
  );
}

export function useSettings() {
  return useContext(SettingsContext);
}
